<script setup lang="ts">
import {
  useUpdateCanvassHouse,
  useUpdateHouse,
} from "@/canvasses/canvassHousesCrud";
import type { Canvass, CanvassHouse } from "@/canvasses/types";
import CounterInput from "@/components/CounterInput.vue";
import SaveButton from "@/components/SaveButton.vue";
import SwitchButton from "@/components/SwitchButton.vue";
import HouseFormFields from "@/houses/HouseFormFields.vue";
import type { House } from "@/houses/types";
import { useDebounceFn } from "@vueuse/core";
import confetti from "canvas-confetti";
import { isEqual } from "lodash-es";
import { computed, ref, toRaw, watch } from "vue";

const props = defineProps<{
  canvass: Canvass;
  canvassHouse: CanvassHouse;
}>();

const canvassHouse = ref(structuredClone(toRaw(props.canvassHouse)));

watch(
  () => canvassHouse.value.completed,
  (completed) => {
    if (completed === true) {
      confetti({ particleCount: 111, disableForReducedMotion: true });
    }
  },
);

const hasChanges = computed(
  () => !isEqual(canvassHouse.value, props.canvassHouse),
);

const mergeCanvassHouse = (house: House) => {
  canvassHouse.value = {
    ...canvassHouse.value,
    ...house,
  };
};

const { updateCanvassHouse } = useUpdateCanvassHouse(props.canvass.id);
const { updateHouse, updateHouseStatus } = useUpdateHouse(props.canvass.id);

const debouncedSave = useDebounceFn(() => {
  updateCanvassHouse(canvassHouse.value);
}, 500);

const updateDoorsOpened = (doorsOpened: number): void => {
  canvassHouse.value.doorsOpened = doorsOpened;
  debouncedSave();
};

const updateDoorsKnocked = (doorsKnocked: number): void => {
  canvassHouse.value.doorsKnocked = doorsKnocked;
  debouncedSave();
};

const setCompleted = (completed: boolean) => {
  canvassHouse.value.completed = completed;
  updateCanvassHouse(canvassHouse.value);
};
</script>
<template>
  <section v-if="canvassHouse !== undefined">
    <form @submit.prevent="updateHouse(canvassHouse)">
      <div>
        <fieldset>
          <legend class="visually-hidden">Heutige Aktion</legend>
          <div class="row">
            <SwitchButton
              id="switch-completed"
              :value="canvassHouse.completed"
              label="Haus als erledigt markieren."
              @checked="setCompleted(true)"
              @unchecked="setCompleted(false)"
            />
          </div>
          <div class="multi-columns multi-columns-xs">
            <div>
              <label for="doors-knocked">Türen geklopft</label>
              <CounterInput
                :min="0"
                :max="canvassHouse.numberOfDoors ?? undefined"
                name="doors-knocked"
                :model-value="canvassHouse.doorsKnocked"
                @update:model-value="updateDoorsKnocked"
              />
            </div>
            <div>
              <label for="doors-knocked">Türen geöffnet</label>
              <CounterInput
                :min="0"
                :max="canvassHouse.numberOfDoors ?? undefined"
                name="doors-opened"
                :model-value="canvassHouse.doorsOpened"
                @update:model-value="updateDoorsOpened"
              />
            </div>
          </div>
        </fieldset>
      </div>

      <fieldset class="houseformfields">
        <legend class="visually-hidden">Allgemeine Adressdaten</legend>
        <HouseFormFields
          :model-value="canvassHouse"
          @update:model-value="mergeCanvassHouse"
        />
      </fieldset>
      <div>
        <SaveButton :saving-status="updateHouseStatus" :disabled="!hasChanges"
          >Änderungen speichern</SaveButton
        >
      </div>
    </form>
  </section>
</template>

<style scoped>
.houseformfields {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  padding-block: var(--spacing-md);
}
</style>
